import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { API_URL } from "../..";

const TimesheetFilters = ({
  activeView,
  setActiveView,
  selectedDate,
  setSelectedDate,
  selectedEmployee,
  setSelectedEmployee,
  rangeStart,
  setRangeStart,
  rangeEnd,
  setRangeEnd,
}) => {
  const [employees, setEmployees] = useState([]);
  const today = new Date();

  // ============================
  // ⬇️ LOAD SAVED FILTERS ON FIRST RENDER
  // ============================
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("ts_filters") || "{}");

    if (saved.activeView) setActiveView(saved.activeView);
    if (saved.selectedEmployee) setSelectedEmployee(saved.selectedEmployee);

    if (saved.selectedDate) setSelectedDate(new Date(saved.selectedDate));
    if (saved.rangeStart) setRangeStart(new Date(saved.rangeStart));
    if (saved.rangeEnd) setRangeEnd(new Date(saved.rangeEnd));
  }, []);

  // ============================
  // ⬆️ SAVE FILTERS WHEN THEY CHANGE
  // ============================
  useEffect(() => {
    const toSave = {
      activeView,
      selectedEmployee,
      selectedDate: selectedDate ? selectedDate.toISOString() : null,
      rangeStart: rangeStart ? rangeStart.toISOString() : null,
      rangeEnd: rangeEnd ? rangeEnd.toISOString() : null,
    };

    localStorage.setItem("ts_filters", JSON.stringify(toSave));
  }, [activeView, selectedEmployee, selectedDate, rangeStart, rangeEnd]);

  useEffect(() => {
    fetch(API_URL + "/api/user/fetch")
      .then((res) => res.json())
      .then((data) => setEmployees(data.data || []))
      .catch(console.error);
  }, []);

  const employeeOptions = employees.map((emp) => ({
    value: emp.id,
    label: emp.name,
  }));

  const selectedOption =
    employeeOptions.find((opt) => opt.value === Number(selectedEmployee)) || null;

  // 🟦 WEEKLY CHANGE
  const handleWeekChange = (date) => {
    if (!date || date > today) return;

    const start = moment(date).startOf("isoWeek").toDate();
    const end = moment(date).endOf("isoWeek").toDate();

    if (end > today) {
      const newStart = moment(today).startOf("isoWeek").toDate();
      setRangeStart(newStart);
      setRangeEnd(today);
      setSelectedDate(newStart);
    } else {
      setRangeStart(start);
      setRangeEnd(end);
      setSelectedDate(start);
    }
  };

  // 🟩 MONTHLY CHANGE
  const handleMonthChange = (date) => {
    if (!date || date > today) return;
    const start = moment(date).startOf("month").toDate();
    setSelectedDate(start);
  };

  // Highlight week selection
  const highlightWeek = (date) => {
    if (!rangeStart || !rangeEnd) return false;
    return moment(date).isBetween(rangeStart, rangeEnd, "day", "[]");
  };

  return (
    <div className="card shadow-sm p-3">
      <div className="d-flex flex-wrap align-items-center justify-content-between">
        {/* Tabs */}
        <ul className="nav nav-pills mb-2">
          {["daily", "weekly", "monthly", "custom"].map((v) => (
            <li key={v} className="nav-item">
              <button
                className={`nav-link ${activeView === v ? "active" : ""}`}
                onClick={() => setActiveView(v)}
              >
                {v === "custom"
                  ? "Custom Range"
                  : v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            </li>
          ))}
        </ul>

        {/* Filters */}
        <div className="d-flex flex-wrap align-items-center gap-3">
          {/* Date Pickers */}
          {activeView === "custom" ? (
            <div className="d-flex align-items-center gap-2">
              <div className="input-group" style={{ width: 150 }}>
                <DatePicker
                  className="form-control form-control-sm"
                  selected={rangeStart}
                  onChange={(d) => d && d <= today && setRangeStart(d)}
                  selectsStart
                  startDate={rangeStart}
                  endDate={rangeEnd}
                  maxDate={today}
                  dateFormat="yyyy-MM-dd"
                />
              </div>
              <span className="fw-bold">to</span>
              <div className="input-group" style={{ width: 150 }}>
                <DatePicker
                  className="form-control form-control-sm"
                  selected={rangeEnd}
                  onChange={(d) => d && d <= today && setRangeEnd(d)}
                  selectsEnd
                  startDate={rangeStart}
                  endDate={rangeEnd}
                  minDate={rangeStart}
                  maxDate={today}
                  dateFormat="yyyy-MM-dd"
                />
              </div>
            </div>
          ) : activeView === "weekly" ? (
            <div className="input-group" style={{ width: 220 }}>
              <DatePicker
                className="form-control form-control-sm"
                selected={selectedDate}
                onChange={handleWeekChange}
                dateFormat="dd MMM yyyy"
                maxDate={today}
                showWeekNumbers
                dayClassName={(d) =>
                  highlightWeek(d) ? "bg-primary text-white rounded-circle" : undefined
                }
              />
            </div>
          ) : activeView === "monthly" ? (
            <div className="input-group" style={{ width: 220 }}>
              <DatePicker
                className="form-control form-control-sm"
                selected={selectedDate}
                onChange={handleMonthChange}
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                maxDate={today}
              />
            </div>
          ) : (
            <div className="input-group" style={{ width: 220 }}>
              <DatePicker
                className="form-control form-control-sm"
                selected={selectedDate}
                onChange={(d) => d && d <= today && setSelectedDate(d)}
                dateFormat="dd MMM yyyy"
                maxDate={today}
              />
            </div>
          )}

          {/* Employee Picker */}
          <div style={{ minWidth: 250 }}>
            <Select
              classNamePrefix="react-select"
              value={selectedOption}
              onChange={(option) =>
                setSelectedEmployee(option ? option.value : "")
              }
              options={[
                { value: "", label: "All Employees" },
                ...employeeOptions,
              ]}
              isClearable
              isSearchable
            />
          </div>
        </div>
      </div>

      {activeView === "weekly" && rangeStart && rangeEnd && (
        <div className="mt-2 text-muted small">
          Showing week:{" "}
          <strong>{moment(rangeStart).format("DD MMM")}</strong> –{" "}
          <strong>{moment(rangeEnd).format("DD MMM YYYY")}</strong>
        </div>
      )}
    </div>
  );
};

export default TimesheetFilters;
